import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./styles/Landing.module.css";
import { useNavigate } from "@tanstack/react-router";
import { selectIsAuthenticated } from "@features/DiscordAuth/model/selectors";
import { useGetChooseFreeSkinMutation, useGetOnboardingProgressQuery } from "@entities/Onboarding/Boardingapi";
import { useGuestLoginMutation } from "@features/GuestAuth/api/guestAuthApiSlice";
import { setCredentials } from "@features/DiscordAuth/model/authSlice";
import { useGetFreeSkinsListMutation, useGetDiscordLoginUrlMutation } from "@features/DiscordAuth/api/authApiSlice";

import useWindowWidth from "@shared/hooks/useWindowWidth"; // Импортируем хук

const MOBILE_BREAKPOINT = 768; // Определяем breakpoint

/**
 * Компонент лендинг-страницы
 */
const Landing: React.FC = () => {
  const navigate = useNavigate();
  const windowWidth = useWindowWidth();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { data: onboardingProgress } = useGetOnboardingProgressQuery(undefined, { skip: !isAuthenticated });
  const [guestLogin, { isLoading: isGuestLoginLoading }] =
    useGuestLoginMutation();
  const [getDiscordLoginUrl] = useGetDiscordLoginUrlMutation();
  const dispatch = useDispatch();
  const [skins, setSkins] = useState<string[]>([]);
  const [skinImages, setSkinImages] = useState<string[]>([]);
  const [currentSkinIndex, setCurrentSkinIndex] = useState(0);
  const [getFreeSkinsList] = useGetFreeSkinsListMutation();
  const [chooseFreeSkin] = useGetChooseFreeSkinMutation();

  useEffect(() => {
    if (isAuthenticated && onboardingProgress?.hasFirstSkin) {
      navigate({ to: "/lobby" });
    }
  }, [isAuthenticated, onboardingProgress, navigate]);

  useEffect(() => {
    getFreeSkinsList()
      .unwrap()
      .then((data) => {
        setSkins(data.products.map(skin => skin.skinName));
        setSkinImages(data.products.map(skin => skin.imageLink));
        setCurrentSkinIndex(0);
      });
  }, [getFreeSkinsList]);

  useEffect(() => {
    const chosenSkin = localStorage.getItem('chosenSkin');
    if (chosenSkin && isAuthenticated) {
      handleChooseSkin(chosenSkin);
    }
  }, [skins, isAuthenticated]);

  // Переход на страницу соревнований
  const handleSquareClick = () => {
    navigate({ to: "/demo" });
  };

  const handleGuestLogin = () => {
    guestLogin()
      .unwrap()
      .then((response) => {
        dispatch(setCredentials(response));
        navigate({ to: "/lobby", replace: true });
      })
      .catch((error) => {
        console.error("Guest login failed:", error);
      });
  };

  const handleChooseSkin = (skinName: string) => {
    chooseFreeSkin(skinName)
      .unwrap()
      .then((response) => {
        console.log("Skin chosen:", response);
      })  
  };

  // Переключение на предыдущий слайд
  const handlePrevSlide = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentSkinIndex((prev) => (prev === 0 ? skins.length - 1 : prev - 1));
  };

  // Переключение на следующий слайд
  const handleNextSlide = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentSkinIndex((prev) => (prev === skins.length - 1 ? 0 : prev + 1));
  };

  // Если ширина окна меньше или равна breakpoint, отображаем мобильную версию
  useEffect(() => {
    if (windowWidth < MOBILE_BREAKPOINT) {
      navigate({ to: "/landing-mobile" });
    }
  }, [windowWidth]);

  const handleDiscordLogin = async () => {
    try {
      const response = await getDiscordLoginUrl().unwrap();
      window.location.href = response.url;
    } catch (error) {
      console.error("Ошибка получения URL для авторизации:", error);
    }
  };

  // Иначе отображаем десктопную версию
  return (
    <div className={styles.landing}>
      <div className={styles.loginButtons} style={isAuthenticated ? { display: 'none' } : {}}>
        <button
          className={styles.guestLoginButton}
          onClick={handleGuestLogin}
          disabled={isGuestLoginLoading}
        >
          {isGuestLoginLoading ? "Вход..." : "Войти как гость"}
        </button>
        <button
          className={styles.discordLoginButton}
          onClick={handleDiscordLogin}
        >
          Войти через Discord
        </button>
      </div>
      <div className={styles.container}>
        <div
          className={styles.squareBlue}
          onClick={() => navigate({ to: "/demo" })}
        />
        <div className={styles.square} onClick={handleSquareClick}>
          <img
            src={`https://community.cloudflare.steamstatic.com/economy/image/${skinImages[currentSkinIndex]}`}
            alt={skins[currentSkinIndex] || "Соревнования"}
            className={styles.squareImage}
          />
          {skins.length > 0 && (
            <>
              <button
                className={styles.chooseSkinButton}
                onClick={e => {
                  e.stopPropagation();
                  localStorage.setItem('chosenSkin', skins[currentSkinIndex]);
                  if (isAuthenticated) {
                    handleChooseSkin(skins[currentSkinIndex]);
                  }
                }}
              >
                Выбрать скин
              </button>
            </>
          )}
        </div>
        <div className={styles.squareSliderControls}>
          <button className={styles.sliderButton} onClick={handlePrevSlide}>
            <img
              src="/landing/Arrow.svg"
              alt="Предыдущий"
              className={styles.sliderButtonImage}
            />
          </button>
          <button className={styles.sliderButton} onClick={handleNextSlide}>
            <img
              src="/landing/Arrow.svg"
              alt="Следующий"
              className={`${styles.sliderButtonImage} ${styles.mirrorImage}`}
            />
          </button>
        </div>
        <div
          className={styles.characterContainer}
          onClick={() => {
            if (onboardingProgress?.hasFirstSkin) {
              navigate({ to: "/lobby" });
            }
          }}
        >
          <img
            src="/landing/Hero.png"
            alt="Персонаж"
            className={styles.character}
          />
        </div>
        <div className={styles.buttonContainer}>
          <button
            className={`${styles.svgButton} ${styles.svgButtonLeft}`}
            onClick={() => navigate({ to: "/demo" })}
          >
            <img
              src="/landing/ButtonBlue.svg"
              alt="Войти"
              className={styles.svgImage}
            />
          </button>
          <button
            className={`${styles.svgButton} ${styles.svgButtonRight}`}
            onClick={handleSquareClick}
          >
            <img
              src="/landing/ButtonRed.svg"
              alt="Соревноваться"
              className={styles.svgImage}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Landing;
